import { JOIN_PATH, tokenFromFragment } from '@scoutaround/script-breakdown-shared';

/**
 * THE INVITATION LINK'S TOKEN, KEPT IN THIS TAB AND NOWHERE ELSE (owner brief
 * "Shareable Project Invitation Links & Approval Workflow", 2026-11-02).
 *
 * A link reads `https://<workspace>/join#<token>`. The token is in the FRAGMENT,
 * which a browser never sends to a server — not in the request line, not in the
 * Referer — so nginx, Traefik and every log between them never see it. Before the
 * router starts (`main.tsx`), the token is moved into this tab's sessionStorage and
 * the address bar is left at `/join`, so nothing the page does afterwards — a
 * screenshot, a copied address, an analytics read of `location.href`, the sign-in
 * round trip through the website — carries it either. API calls send it as JSON.
 *
 * sessionStorage, like the sign-in `state` and the Terms resume: the round trip
 * through the website's sign-in comes back to this same tab, and the token is gone
 * the moment the tab is closed.
 */
export const JOIN_TOKEN_KEY = 'sb.join_token';

/** Set when the person pressed "Request to join" signed out: ask once, on the way back. */
export const JOIN_INTENT_KEY = 'sb.join_intent';

function isJoinPath(pathname: string): boolean {
  return /^\/+join\/?$/i.test(pathname);
}

/**
 * Move a token in the fragment of `/join` into this tab, and clean the address.
 * Run once, first thing, before the router exists. Anything else in a `/join`
 * fragment is dropped too: it is not ours to keep.
 */
export function takeJoinTokenFromLocation(win: Window = window): void {
  const { pathname, search, hash } = win.location;
  if (!isJoinPath(pathname) || hash === '' || hash === '#') return;

  const token = tokenFromFragment(hash);
  if (token !== null) {
    try {
      win.sessionStorage.setItem(JOIN_TOKEN_KEY, token);
      // A new link is a new question: whatever this tab meant to do with another one is void.
      win.sessionStorage.removeItem(JOIN_INTENT_KEY);
    } catch {
      // Storage refused (a private window): the page explains the link cannot be kept.
    }
  }

  win.history.replaceState(win.history.state, '', `${JOIN_PATH}${search}`);
}

export function heldJoinToken(): string | null {
  try {
    const token = sessionStorage.getItem(JOIN_TOKEN_KEY);
    return token !== null && tokenFromFragment(token) !== null ? token : null;
  } catch {
    return null;
  }
}

export function forgetJoinToken(): void {
  try {
    sessionStorage.removeItem(JOIN_TOKEN_KEY);
    sessionStorage.removeItem(JOIN_INTENT_KEY);
  } catch {
    /* nothing kept */
  }
}

export function markJoinIntent(): void {
  try {
    sessionStorage.setItem(JOIN_INTENT_KEY, '1');
  } catch {
    /* the person presses once more on the way back */
  }
}

/** Read once and cleared, so a request is never made twice on its own. */
export function takeJoinIntent(): boolean {
  try {
    const intent = sessionStorage.getItem(JOIN_INTENT_KEY) === '1';
    sessionStorage.removeItem(JOIN_INTENT_KEY);
    return intent;
  } catch {
    return false;
  }
}
